import toast from 'react-hot-toast';

/**
 * Interruttore IA dell'azienda: testi e riconoscimento dell'errore 403 ai_disabled restituito
 * da edge function e flusia-core. Modulo senza dipendenze dallo store (lo usa anche
 * lib/supabase.ts per il toast globale sulle edge function).
 */

export const AI_DISABLED_TOOLTIP = "IA spenta per l'azienda (Impostazioni › Intelligenza artificiale)";
export const AI_DISABLED_MESSAGE = 'Le funzioni IA sono spente per questa azienda';
export const AI_DISABLED_CODE = 'ai_disabled';

/**
 * L'errore arriva da una funzione IA che ha risposto 403 ai_disabled? Copre coreCall
 * ({ code, status }), le risposte gia' lette ({ error: 'ai_disabled' }) e i messaggi.
 * Per supabase.functions.invoke usare aiDisabledFromInvokeError (il corpo va letto).
 */
export function isAiDisabledError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { code?: unknown; error?: unknown; message?: unknown };
  if (e.code === AI_DISABLED_CODE || e.error === AI_DISABLED_CODE) return true;
  const msg = typeof e.message === 'string' ? e.message : typeof err === 'string' ? err : '';
  return msg === AI_DISABLED_CODE || msg.includes(AI_DISABLED_MESSAGE);
}

/**
 * Errore di supabase.functions.invoke (FunctionsHttpError: il corpo e' in error.context).
 * Ritorna true se era ai_disabled e in quel caso mostra gia' il toast.
 */
export async function aiDisabledFromInvokeError(error: unknown): Promise<boolean> {
  if (!error) return false;
  if (isAiDisabledError(error)) {
    notifyAiDisabled();
    return true;
  }
  const ctx = (error as { context?: unknown }).context;
  if (ctx && typeof (ctx as Response).clone === 'function') {
    try {
      const res = ctx as Response;
      if (res.status !== 403) return false;
      const body = await res.clone().json();
      if (isAiDisabledError(body)) {
        notifyAiDisabled();
        return true;
      }
    } catch {
      return false;
    }
  }
  return false;
}

/** Toast unico per tutto il CRM (stesso id: non si accumulano). */
export function notifyAiDisabled(): void {
  toast.error(AI_DISABLED_MESSAGE, { id: 'ai-disabled' });
}

/**
 * fetch per il client Supabase: se una edge function risponde 403 ai_disabled mostra il toast
 * (una volta, stesso id). La risposta torna intatta al chiamante.
 */
export const aiDisabledAwareFetch: typeof fetch = async (input, init) => {
  const res = await fetch(input, init);
  if (res.status === 403) {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
    if (url.includes('/functions/v1/')) {
      res.clone().json().then((body) => {
        if (isAiDisabledError(body)) notifyAiDisabled();
      }).catch(() => undefined);
    }
  }
  return res;
};
